import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiClient, { API_BASE_URL } from '../utils/apiClient';
import logger from '../utils/logger';
import { useWordContext } from '../context/WordContext';
import { normalizeWord } from '../utils/wordUtils';
import { ReactComponent as SpeakerIcon } from '../images/icons/speaker.svg';
import './ChatbotDictionary.css'; // We'll create this CSS file specifically for chatbot dictionary
import './ChatbotDictionaryGrading.css'; // Import dedicated grading CSS

// Module-level cache of fetched word definitions. The chatbot dictionary panel is
// unmounted whenever the user closes the window, which resets the component's own
// `definition` state and would otherwise trigger a fresh Gemini/API call each time
// the same word is clicked again. Caching here lets re-clicks reuse the result,
// matching the persistent component behavior used on Home/Stories.
const definitionCache = new Map();
const MAX_DEFINITION_CACHE_ENTRIES = 200;

const getDefinitionCacheKey = (language, baseLanguage, word, sentence) =>
    `${language}|${baseLanguage || 'English'}|${word}|${sentence || ''}`;

// Helper function to extract the specific sentence containing the target word
const extractSentenceContext = (fullText, targetWord) => {
    try {
        if (!fullText || !targetWord) return fullText;

        const targetIndex = fullText.indexOf(targetWord);

        if (targetIndex === -1) return fullText;

        // Find sentence boundaries (., !, ?)
        // Start by finding the beginning of the sentence
        let startIndex = targetIndex;
        while (startIndex > 0) {
            // Look for sentence-ending punctuation followed by space or start of text
            if (/[.!?]\s/.test(fullText.substring(startIndex - 1, startIndex + 1)) ||
                startIndex === 1) {
                break;
            }
            startIndex--;
        }

        // Find the end of the sentence
        let endIndex = targetIndex + targetWord.length;
        while (endIndex < fullText.length) {
            // Look for sentence-ending punctuation
            if (/[.!?]/.test(fullText[endIndex]) &&
                (endIndex + 1 === fullText.length || /\s/.test(fullText[endIndex + 1]))) {
                endIndex++; // Include the punctuation
                break;
            }
            endIndex++;
        }

        // Get the complete sentence
        return fullText.substring(startIndex, endIndex).trim();
    } catch (err) {
        logger.error(`ChatbotWordLookup - Error extracting context for "${targetWord}":`, err);
        return fullText;
    }
};

// Helper function to format definition text with special handling for parenthetical content
const formatDefinition = (text) => {
    if (!text) return '';

    // Find all parenthetical expressions
    const parts = [];
    let currentText = '';
    let inParentheses = 0;
    let parentheticalText = '';

    for (let i = 0; i < text.length; i++) {
        const char = text[i];

        if (char === '(') {
            if (inParentheses === 0) {
                // Save regular text before parentheses
                if (currentText) {
                    parts.push(<span key={`text-${parts.length}`}>{currentText}</span>);
                    currentText = '';
                }
                parentheticalText = '(';
            } else {
                parentheticalText += '(';
            }
            inParentheses++;
        } else if (char === ')' && inParentheses > 0) {
            parentheticalText += ')';
            inParentheses--;

            if (inParentheses === 0) {
                // Save complete parenthetical expression
                parts.push(<span key={`parens-${parts.length}`} className="chatbot-parenthetical">{parentheticalText}</span>);
                parentheticalText = '';
            }
        } else if (inParentheses > 0) {
            parentheticalText += char;
        } else {
            currentText += char;
        }
    }

    // Add any remaining text
    if (currentText) {
        parts.push(<span key={`text-${parts.length}`}>{currentText}</span>);
    }

    // Add any unclosed parenthetical content
    if (parentheticalText) {
        parts.push(<span key={`parens-${parts.length}`} className="chatbot-parenthetical">{parentheticalText}</span>);
    }

    return <>{parts}</>;
};

// Verb tenses offered by the "Conjugations" section of the word popup. These mirror
// the Beginner / Intermediate / Advanced groupings used on the Home page so a learner
// sees the same categories in both places. Only languages with a curated list here
// expose the Conjugations section.
const VERB_TENSES_BY_LANGUAGE = {
    Spanish: {
        Beginner: [
            "Present (Presente de Indicativo)",
            "Preterite (Pretérito Indefinido)",
            "Imperfect (Pretérito Imperfecto)",
            "Future Simple (Futuro Simple)",
            "Conditional (Condicional Simple)",
            "Imperative (Imperativo)",
            "Present Perfect (Pretérito Perfecto)"
        ],
        Intermediate: [
            "Subjunctive Present (Presente de Subjuntivo)",
            "Past Perfect (Pretérito Pluscuamperfecto)",
            "Future Perfect (Futuro Perfecto)",
            "Conditional Perfect (Condicional Compuesto)",
            "Imperfect Subjunctive (Pretérito Imperfecto de Subjuntivo)"
        ],
        Advanced: [
            "Past Perfect Subjunctive (Pluscuamperfecto de Subjuntivo)",
            "Future Subjunctive (Futuro de Subjuntivo)"
        ]
    },
    French: {
        Beginner: ["Présent", "Passé Composé", "Imparfait"],
        Intermediate: ["Futur Simple", "Conditionnel Présent", "Subjonctif Présent"],
        Advanced: ["Plus-que-parfait", "Passé Simple", "Passé Antérieur", "Conditionnel Passé", "Futur Antérieur"]
    },
    Italian: {
        Beginner: ["Presente", "Passato Prossimo", "Imperfetto", "Futuro Semplice", "Condizionale Presente"],
        Intermediate: ["Congiuntivo Presente", "Trapassato Prossimo", "Futuro Anteriore", "Condizionale Passato"],
        Advanced: ["Congiuntivo Imperfetto", "Congiuntivo Trapassato"]
    }
};

// Returns true when a fetched definition describes a verb, so the popup can decide
// whether to offer its Conjugations section.
const isVerbDefinition = (def) => {
    if (!def || typeof def !== 'object') return false;
    if (def.parts_of_speech && def.parts_of_speech.verb) return true;
    if (def.verb_info && (def.verb_info.infinitive_form || def.verb_info.tense)) return true;
    if (Array.isArray(def.definitions) &&
        def.definitions.some(d => typeof d === 'string' && /^verb\b/i.test(d.trim()))) {
        return true;
    }
    return false;
};

const ChatbotWordLookup = ({ word, children, language, sentence, onSelectWord, baseLanguage: baseLanguageProp, lemmatizeSavedWord = true }) => {
    const [definition, setDefinition] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastClickedWord, setLastClickedWord] = useState(null);
    // Track which accordion section is open (null, 'context', 'grammar', 'conjugation', or 'examples')
    const [openAccordion, setOpenAccordion] = useState(null);
    // --- Conjugation section state ---
    // Which tense's conjugation is currently displayed (null => the tense list view).
    const [activeConjugationTense, setActiveConjugationTense] = useState(null);
    // Cache of already-generated conjugations, keyed by tense name. Once a tense has been
    // generated it is shown instantly on subsequent opens without another LLM request.
    const [conjugations, setConjugations] = useState({});
    const [conjugationLoading, setConjugationLoading] = useState(false);
    const [conjugationError, setConjugationError] = useState(null);
    const [wordGrade, setWordGrade] = useState(1);
    const [wordKnown, setWordKnown] = useState(false);
    const [justSavedWord, setJustSavedWord] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const cachedAudioUrl = useRef(null);
    // Reference to the Audio instance currently playing. Because the dictionary panel now
    // stays mounted across word changes (so definitions stay cached), we must explicitly
    // stop a previous word's audio when a new word is displayed — otherwise the old audio
    // keeps playing ("stuck") and its cached URL would leak into the next word.
    const activeAudioRef = useRef(null);
    // Search-bar state (dictionary panel): lets the user look up another word
    // without leaving the panel. Kept separate from the clicked-word definition
    // so the original definition is restored when the search is cleared.
    const [searchTerm, setSearchTerm] = useState('');
    const [searchedFor, setSearchedFor] = useState('');
    const [searchDefinition, setSearchDefinition] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [isSearchMode, setIsSearchMode] = useState(false);
    // Saved/grade/known state for the searched result (kept separate from the clicked
    // word so searching never changes the clicked word's highlight in the text).
    const [searchIsSaved, setSearchIsSaved] = useState(false);
    const [searchGrade, setSearchGrade] = useState(1);
    const [searchKnown, setSearchKnown] = useState(false);
    // Track words whose missing forms we've already tried to backfill this mount,
    // to avoid duplicate POSTs while the context state settles.
    const backfilledFormsRef = useRef(new Set());
    // Identifies the word/verb currently displayed in the panel (clicked word OR search
    // result). Because the panel stays mounted across displayed-word changes, an in-flight
    // conjugation request for a previously displayed verb could resolve after the user has
    // moved on and leak its result into the new verb. Capturing this key lets us discard
    // such stale responses.
    const displayKeyRef = useRef('');

    // Use the context for saved words
    const {
        isWordSaved,
        addSavedWord,
        removeSavedWord,
        refreshSavedWords,
        getWordGrade,
        isWordKnown,
        updateWordGrade,
        updateWordKnown,
        isKanjiSaved,
        isKanjiKnown,
        getKanjiGrade,
        getSavedFormMatch,
        savedWordsDetails,
        backfillSavedWordForms
    } = useWordContext();

    // Normalize the current word for checking
    const normalizedWord = normalizeWord(word);

    // Get current language
    const currentLanguage = language || localStorage.getItem('learningLanguage') || 'Spanish';
    const baseLanguage = baseLanguageProp || localStorage.getItem('baseLanguage') || 'English';

    // LEMMA MODE: when the fetched definition provides a base form (infinitive for verbs,
    // singular for nouns), use that base form as the key for saved-status checks and for
    // saving, so any conjugation/plural resolves to the same saved entry.
    const dictionaryLemma = (lemmatizeSavedWord && definition)
        ? String(definition.lemma || definition.verb_info?.infinitive_form || '').trim()
        : '';
    const lemmaKey = dictionaryLemma ? normalizeWord(dictionaryLemma) : '';
    // Inflected surface forms returned by Gemini for the lemma (used both for instant
    // highlighting and to persist alongside the saved word). We always include the exact
    // surface word the user interacted with so it can be re-highlighted after a refresh
    // even if the model omitted that particular form from its list.
    const dictionaryForms = lemmatizeSavedWord
        ? Array.from(new Set([
            ...((definition && Array.isArray(definition.forms)) ? definition.forms : [])
                .map(f => normalizeWord(String(f)))
                .filter(Boolean),
            normalizedWord
        ].filter(Boolean)))
        : [];
    // INSTANT MATCH: if this surface word is a known inflection of an already-saved word,
    // resolve it to that saved entry immediately (no LLM call needed for highlighting).
    const formMatch = lemmatizeSavedWord ? getSavedFormMatch(normalizedWord, currentLanguage) : null;
    // The key used for saved-status lookups, saving, deleting, grading and known-toggling.
    const matchWord = formMatch || lemmaKey || normalizedWord;

    // --- Search-result (dictionary panel) values ---
    // When the user searches from the panel, the saved-status/save/delete/grading
    // actions must target the searched result instead of the clicked word. Search
    // results always carry a `lemma` (base form) and `forms`, so the same lemma-based
    // logic applies. These values are kept separate from the clicked-word values above
    // so a search never alters the clicked word's highlight in the text.
    const searchLemma = searchDefinition
        ? String(searchDefinition.lemma || '').trim()
        : '';
    const searchNormalizedWord = searchDefinition
        ? normalizeWord(String(searchDefinition.lemma || searchedFor || '').trim())
        : (searchedFor ? normalizeWord(searchedFor) : '');
    const searchLemmaKey = searchLemma ? normalizeWord(searchLemma) : '';
    const searchForms = Array.from(new Set([
        ...((searchDefinition && Array.isArray(searchDefinition.forms)) ? searchDefinition.forms : [])
            .map(f => normalizeWord(String(f)))
            .filter(Boolean),
        searchNormalizedWord
    ].filter(Boolean)));
    const searchFormMatch = searchNormalizedWord ? getSavedFormMatch(searchNormalizedWord, currentLanguage) : null;
    const searchMatchWord = searchFormMatch || searchLemmaKey || searchNormalizedWord;

    // Check if the word is saved (declared before the active selectors that read it)
    const [isSaved, setIsSaved] = useState(false);

    // The values the panel UI and the saved-word actions operate on right now.
    const activeMatchWord = isSearchMode ? searchMatchWord : matchWord;
    const activeLemma = isSearchMode ? searchLemma : dictionaryLemma;
    const activeForms = isSearchMode ? searchForms : dictionaryForms;
    const activeIsSaved = isSearchMode ? searchIsSaved : isSaved;
    const activeGrade = isSearchMode ? searchGrade : wordGrade;
    const activeKnown = isSearchMode ? searchKnown : wordKnown;

    // Keep the searched result's saved/grade/known state in sync. This is independent
    // of the clicked word so the clicked word's highlight is never affected by a search.
    useEffect(() => {
        if (!isSearchMode || !searchMatchWord) return;
        const saved = isWordSaved(searchMatchWord, currentLanguage);
        setSearchIsSaved(saved);
        if (saved) {
            setSearchGrade(getWordGrade(searchMatchWord, currentLanguage));
            setSearchKnown(isWordKnown(searchMatchWord, currentLanguage));
        } else {
            setSearchGrade(1);
            setSearchKnown(false);
        }
    }, [isSearchMode, searchMatchWord, currentLanguage, isWordSaved, getWordGrade, isWordKnown, savedWordsDetails]);

    // Check if the word is saved
    const checkSavedStatus = useCallback(() => {
        const exactMatch = isWordSaved(matchWord, currentLanguage);
        if (exactMatch || justSavedWord) {
            return true;
        }
        return false;
    }, [matchWord, currentLanguage, isWordSaved, justSavedWord]);

    // Update the saved status when data changes
    useEffect(() => {
        const savedStatus = checkSavedStatus();
        setIsSaved(savedStatus);
    }, [checkSavedStatus, matchWord, currentLanguage, justSavedWord]);

    // Update local state when the word's saved status changes
    useEffect(() => {
        if (isSaved) {
            setWordGrade(getWordGrade(matchWord, currentLanguage));
            setWordKnown(isWordKnown(matchWord, currentLanguage));
        } else {
            setWordGrade(1);
            setWordKnown(false);
        }
    }, [isSaved, matchWord, currentLanguage, getWordGrade, isWordKnown]);

    // Reset justSavedWord (and any in-progress search) when word changes
    useEffect(() => {
        // When the word changes, reset justSavedWord to false
        setJustSavedWord(false);
        // Clear any in-progress search so a new word starts fresh.
        setSearchTerm('');
        setSearchedFor('');
        setSearchDefinition(null);
        setSearchError(null);
        setIsSearchMode(false);
        setSearchIsSaved(false);
        setSearchGrade(1);
        setSearchKnown(false);
    }, [normalizedWord]);

    // Reset the accordion, conjugation AND audio state whenever the displayed word/verb
    // changes, so a previous word's open section, generated tenses, or playing/cached audio
    // never leak into the next word. This must key off the search result (`searchedFor`) and
    // search mode as well as the clicked `word`, because searching a new term changes what is
    // displayed without changing the clicked word prop — that was letting a previously
    // generated conjugation persist into the newly searched verb.
    useEffect(() => {
        // Stop any audio still playing from the previous word.
        if (activeAudioRef.current) {
            try {
                activeAudioRef.current.pause();
                activeAudioRef.current.currentTime = 0;
            } catch (e) {
                // ignore: the audio element may already be detached
            }
            activeAudioRef.current = null;
        }
        setIsPlaying(false);
        // Clear the cached audio URL so the new word does not replay the previous word's audio.
        cachedAudioUrl.current = null;
        setOpenAccordion(null);
        setActiveConjugationTense(null);
        setConjugations({});
        setConjugationLoading(false);
        setConjugationError(null);
        // Record which word/verb is now displayed so any late-arriving response for the
        // previous word can be detected and ignored in handleSelectTense.
        displayKeyRef.current = isSearchMode ? `search:${searchedFor}` : `word:${word}`;
    }, [word, sentence, searchedFor, isSearchMode]);

    // Stop any audio when the component unmounts (e.g. the dictionary overlay is fully closed).
    useEffect(() => {
        return () => {
            if (activeAudioRef.current) {
                try {
                    activeAudioRef.current.pause();
                    activeAudioRef.current.currentTime = 0;
                } catch (e) {
                    // ignore
                }
                activeAudioRef.current = null;
            }
        };
    }, []);

    // BACKFILL: if this word is already saved but its stored inflected forms are missing
    // (legacy saves made before the precomputed-forms feature), persist the forms we just
    // learned from the definition so conjugations/plurals keep highlighting after a refresh.
    useEffect(() => {
        if (!lemmatizeSavedWord || !definition || !isSaved || !matchWord) return;
        const formsFromDef = Array.isArray(definition.forms) ? definition.forms : [];
        const formsToStore = Array.from(new Set([
            ...formsFromDef.map(f => normalizeWord(String(f))).filter(Boolean),
            normalizedWord
        ].filter(Boolean)));
        if (formsToStore.length === 0) return;
        const savedForms = (savedWordsDetails?.[currentLanguage]?.[matchWord]?.forms) || [];
        const savedKeys = new Set(savedForms.map(f => normalizeWord(String(f))));
        const missing = formsToStore.filter(f => !savedKeys.has(normalizeWord(f)));
        if (missing.length === 0) return;
        const guardKey = `${currentLanguage}:${matchWord}`;
        if (backfilledFormsRef.current.has(guardKey)) return;
        backfilledFormsRef.current.add(guardKey);
        backfillSavedWordForms(matchWord, currentLanguage, {
            lemma: dictionaryLemma || matchWord,
            forms: formsToStore
        });
    }, [lemmatizeSavedWord, definition, isSaved, matchWord, currentLanguage, normalizedWord, dictionaryLemma, savedWordsDetails, backfillSavedWordForms]);

    // Helper function to get userId from multiple sources
    const getUserId = () => {
        // Always try to get the latest userId from the JWT token first
        const token = localStorage.getItem('access_token');
        if (token) {
            try {
                // Decode JWT token to get user info
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                }).join(''));

                const decodedToken = JSON.parse(jsonPayload);
                if (decodedToken.sub) {
                    const userId = decodedToken.sub;
                    logger.debug(`ChatbotWordLookup - Extracted user ID from token: ${userId}`);
                    localStorage.setItem('userId', userId);
                    return userId;
                }
            } catch (err) {
                logger.error("ChatbotWordLookup - Error decoding token:", err);
            }
        }

        // Fall back to localStorage only if token extraction fails
        const localUserId = localStorage.getItem('userId');
        if (localUserId) {
            logger.debug("ChatbotWordLookup - Using userId from localStorage (fallback)");
            return localUserId;
        }

        logger.warn("ChatbotWordLookup - No userId available from token or localStorage");
        return null;
    };

    const fetchDefinition = async () => {
        // If we've already fetched this word and it's being reopened, don't fetch again
        if (lastClickedWord === normalizedWord && definition) {
            logger.debugComponent('chatbot', `Using cached definition for "${normalizedWord}"`);
            return;
        }

        // Reuse a definition fetched earlier (e.g. before the dictionary window was
        // closed and this component was unmounted) instead of calling the API again.
        const cacheKey = getDefinitionCacheKey(currentLanguage, baseLanguage, normalizedWord, sentence);
        const cachedDefinition = definitionCache.get(cacheKey);
        if (cachedDefinition) {
            logger.debugComponent('chatbot', `Using module-cached definition for "${normalizedWord}"`);
            setDefinition(cachedDefinition);
            setError(null);
            setLoading(false);
            setLastClickedWord(normalizedWord);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            let resolvedDefinition = null;

            // Map full language names to language codes
            const languageMapping = {
                'Japanese': 'ja',
                'Spanish': 'es',
                'French': 'fr',
                'Italian': 'it',
                'German': 'de',
                'Portuguese': 'pt',
                'Chinese': 'zh',
                'Russian': 'ru',
                'Arabic': 'ar',
                'Korean': 'ko',
                'Dutch': 'nl',
                'Polish': 'pl',
                'Swedish': 'sv',
                'Turkish': 'tr',
                'Ukrainian': 'uk',
                'Vietnamese': 'vi',
                'Greek': 'el',
                'Hebrew': 'he',
                'Hindi': 'hi',
                'Thai': 'th'
            };

            // Get language code (lowercase) or use full language name if no mapping
            const languageCode = languageMapping[currentLanguage] || currentLanguage.toLowerCase();

            // For Japanese, we need to handle the API request differently
            if (currentLanguage === 'Japanese') {
                logger.debugComponent('chatbot', `Fetching definition for Japanese word "${word}"`);

                // Use our proxy endpoint for Japanese lookups
                const jishoResponse = await apiClient.get(
                    `/api/jisho?keyword=${encodeURIComponent(word)}`
                );

                if (!jishoResponse.ok) {
                    throw new Error(`Jisho API returned status ${jishoResponse.status}`);
                }

                const jishoData = await jishoResponse.json();
                logger.debugComponent('chatbot', `Received Jisho data for "${word}":`, jishoData);

                // Format the Jisho response into our expected structure
                if (jishoData.data && jishoData.data.length > 0) {
                    const processedData = {
                        definitions: jishoData.data[0].senses.map(sense =>
                            sense.english_definitions.join('; ')
                        ),
                        reading: jishoData.data[0].japanese[0].reading,
                        jlpt: jishoData.data[0].jlpt.join(', '),
                        isJisho: true
                    };
                    setDefinition(processedData);
                    resolvedDefinition = processedData;
                } else {
                    const emptyData = { definitions: [], isJisho: true };
                    setDefinition(emptyData);
                    resolvedDefinition = emptyData;
                }
            } else {
                // Check if we have a sentence context
                if (sentence) {
                    logger.debugComponent('chatbot', `Fetching context definition for "${word}" in sentence: "${sentence}"`);

                    // Use the contextDefine endpoint when we have a sentence
                    const response = await apiClient.get(
                        `/api/contextDefine?word=${encodeURIComponent(word)}&sentence=${encodeURIComponent(sentence)}&lang=${languageCode}&baseLang=${encodeURIComponent(baseLanguage)}`
                    );

                    if (!response.ok) {
                        logger.errorComponent('chatbot', `Context API error status ${response.status} for "${word}"`);
                        throw new Error(`Context definition endpoint returned status ${response.status}`);
                    }

                    const result = await response.json();
                    logger.debugComponent('chatbot', `Received context definition data for "${word}":`, result);

                    // Validate and process the response
                    resolvedDefinition = processDefinitionResponse(response, result, word);
                } else {
                    logger.debugComponent('chatbot', `Fetching standard definition for "${word}"`);

                    // Use the regular define endpoint when we don't have a sentence
                    const response = await apiClient.get(
                        `/api/define?word=${encodeURIComponent(word)}&lang=${languageCode}&baseLang=${encodeURIComponent(baseLanguage)}`
                    );

                    if (!response.ok) {
                        logger.errorComponent('chatbot', `API error status ${response.status} for "${word}"`);
                        throw new Error(`Definition endpoint returned status ${response.status}`);
                    }

                    const result = await response.json();
                    logger.debugComponent('chatbot', `Received definition data for "${word}":`, result);

                    // Validate and process the response
                    resolvedDefinition = processDefinitionResponse(response, result, word);
                }
            }

            // Cache the successful result so re-clicking this word is instant even
            // after the dictionary panel is closed and reopened.
            if (resolvedDefinition) {
                if (definitionCache.size >= MAX_DEFINITION_CACHE_ENTRIES) {
                    // Drop the oldest entry (Map preserves insertion order).
                    definitionCache.delete(definitionCache.keys().next().value);
                }
                definitionCache.set(cacheKey, resolvedDefinition);
            }

            // Update the last clicked word to avoid unnecessary API calls
            setLastClickedWord(normalizedWord);
        } catch (err) {
            logger.errorComponent('chatbot', `Error fetching definition for "${word}":`, err);
            logger.error("ChatbotWordLookup - Error fetching definition:", err);

            // Create a more user-friendly error message
            const errorMessage = err.message || "Failed to fetch definition";
            setError(errorMessage);

            // Set minimal data to avoid null reference errors
            setDefinition({
                definitions: [`Could not load definition for "${word}". ${errorMessage}`]
            });
        } finally {
            setLoading(false);
        }
    };

    // Helper function to process and validate definition response
    const processDefinitionResponse = (response, result, word) => {
        try {
            // Validate the response structure
            if (!result || typeof result !== 'object') {
                logger.errorComponent('chatbot', `Invalid API response format for "${word}":`, result);
                throw new Error('Invalid API response format');
            }

            // Make sure definitions exists and is an array
            if (!Array.isArray(result.definitions)) {
                logger.errorComponent('chatbot', `API response missing definitions array:`, result);
                result.definitions = result.definitions ? [String(result.definitions)] : ['No definition available'];
            }

            // Ensure all definitions are strings
            result.definitions = result.definitions.map(def =>
                typeof def === 'string' ? def : JSON.stringify(def)
            );

            // Check if we have parts_of_speech data
            if (result.parts_of_speech) {
                logger.debugComponent('chatbot', `Word "${word}" parts of speech:`, result.parts_of_speech);

                // If it's a noun, log additional info
                if (result.parts_of_speech.noun && result.noun_info) {
                    logger.debugComponent('chatbot', `Noun info for "${word}":`, result.noun_info);
                }

                // If it's a verb, log additional info
                if (result.parts_of_speech.verb && result.verb_info) {
                    logger.debugComponent('chatbot', `Verb info for "${word}":`, result.verb_info);
                }
            } else {
                logger.debugComponent('chatbot', `No parts of speech data for "${word}"`);
            }

            setDefinition(result);
            return result;
        } catch (jsonError) {
            logger.errorComponent('chatbot', `JSON parse error for "${word}":`, jsonError);
            try {
                logger.debugComponent('chatbot', `Raw response text:`, result);
            } catch (textError) {
                logger.errorComponent('chatbot', `Could not get response text:`, textError);
            }
            const fallback = {
                definitions: [`Error parsing definition data for "${word}"`]
            };
            setDefinition(fallback);
            return fallback;
        }
    };

    // Search-bar lookup: looks up a term the user typed with NO surrounding context.
    // The endpoint resolves the base/citation form (lemma) and, when the term is in
    // the native language, returns the target-language equivalent. Explanations stay
    // in the native language and context is reported as "N/A".
    const handleSearch = async () => {
        const term = searchTerm.trim();
        if (!term) return;

        // Map full language names to language codes (mirrors fetchDefinition).
        const languageMapping = {
            'Japanese': 'ja', 'Spanish': 'es', 'French': 'fr', 'Italian': 'it',
            'German': 'de', 'Portuguese': 'pt', 'Chinese': 'zh', 'Russian': 'ru',
            'Arabic': 'ar', 'Korean': 'ko', 'Dutch': 'nl', 'Polish': 'pl',
            'Swedish': 'sv', 'Turkish': 'tr', 'Ukrainian': 'uk', 'Vietnamese': 'vi',
            'Greek': 'el', 'Hebrew': 'he', 'Hindi': 'hi', 'Thai': 'th'
        };
        const languageCode = languageMapping[currentLanguage] || currentLanguage.toLowerCase();

        setIsSearchMode(true);
        setSearchedFor(term);
        setSearchDefinition(null);
        setSearchError(null);
        setSearchLoading(true);
        setOpenAccordion(null);

        try {
            const response = await apiClient.get(
                `/api/searchDefine?word=${encodeURIComponent(term)}&lang=${languageCode}&baseLang=${encodeURIComponent(baseLanguage)}`
            );

            if (!response.ok) {
                logger.errorComponent('chatbot', `Search API error status ${response.status} for "${term}"`);
                throw new Error(`Search definition endpoint returned status ${response.status}`);
            }

            const result = await response.json();
            logger.debugComponent('chatbot', `Received search definition data for "${term}":`, result);

            if (!result || typeof result !== 'object') {
                throw new Error('Invalid search response format');
            }

            if (!Array.isArray(result.definitions)) {
                result.definitions = result.definitions ? [String(result.definitions)] : ['No definition available'];
            }
            result.definitions = result.definitions.map(def =>
                typeof def === 'string' ? def : JSON.stringify(def)
            );
            // There is no sentence context for a searched word, so always report N/A.
            if (!result.context_note) {
                result.context_note = 'N/A';
            }

            setSearchDefinition(result);
        } catch (err) {
            logger.errorComponent('chatbot', `Error searching definition for "${term}":`, err);
            const errorMessage = err.message || 'Failed to search definition';
            setSearchError(errorMessage);
            setSearchDefinition({
                definitions: [`Could not load definition for "${term}". ${errorMessage}`]
            });
        } finally {
            setSearchLoading(false);
        }
    };

    // Leave search mode and restore the originally-clicked word's definition.
    const handleBackToWord = () => {
        setIsSearchMode(false);
        setSearchTerm('');
        setSearchedFor('');
        setSearchDefinition(null);
        setSearchError(null);
        setOpenAccordion(null);
        setSearchIsSaved(false);
        setSearchGrade(1);
        setSearchKnown(false);
    };

    // Handle click on word to send it to the dictionary panel
    const handleClick = () => {
        // If we're clicking a word, fetch its definition and notify the parent component
        if (!definition || normalizedWord !== lastClickedWord) {
            setLastClickedWord(normalizedWord);
            fetchDefinition();
        }

        // Extract the specific sentence containing the word before notifying the parent component
        const contextSentence = extractSentenceContext(sentence, word);
        logger.debugComponent('chatbot', `Fetching context definition for "${word}" in sentence: "${contextSentence}"`);

        // Notify the parent component about the selected word with the extracted sentence
        if (onSelectWord && typeof onSelectWord === 'function') {
            onSelectWord(word, contextSentence);
        }
    };

    // Handle accordion toggling - only one section can be open at a time
    const toggleAccordion = (section) => {
        setOpenAccordion(openAccordion === section ? null : section);
    };

    // Toggle the Conjugations section. Closing it returns the section to its tense-list
    // view so re-opening starts from the list again (generated tenses stay cached).
    const toggleConjugation = () => {
        const nextOpen = openAccordion === 'conjugation' ? null : 'conjugation';
        setOpenAccordion(nextOpen);
        if (nextOpen === null) {
            setActiveConjugationTense(null);
            setConjugationError(null);
        }
    };

    // Generate (or reuse the cached) conjugation for one tense.
    const handleSelectTense = async (tense) => {
        setActiveConjugationTense(tense);
        setConjugationError(null);

        // Already generated this tense -> reuse the cached result; no LLM call.
        if (conjugations[tense]) return;

        // Snapshot the displayed word/verb for this request. If the panel moves to a
        // different word/verb before the response arrives, the result is stale and must be
        // discarded so it cannot leak into the new verb (e.g. search verb A, open a tense,
        // then search verb B before A's conjugation finishes loading).
        const requestKey = displayKeyRef.current || (isSearchMode ? `search:${searchedFor}` : `word:${word}`);
        const isStale = () => displayKeyRef.current !== requestKey;

        setConjugationLoading(true);
        try {
            const languageMapping = { 'Spanish': 'es', 'French': 'fr', 'Italian': 'it' };
            const languageCode = languageMapping[currentLanguage] || currentLanguage.toLowerCase();

            // Prefer the verb's base form (infinitive) so any clicked inflection resolves
            // to the same conjugation table.
            const defForConj = isSearchMode ? searchDefinition : definition;
            const verbBase = (defForConj && (defForConj.lemma || (defForConj.verb_info && defForConj.verb_info.infinitive_form)))
                || (isSearchMode ? searchedFor : word);

            logger.debug(`ChatbotWordLookup - Requesting conjugation for "${verbBase}" (${tense}) in ${currentLanguage}`);

            const response = await apiClient.get(
                `/api/conjugate?word=${encodeURIComponent(verbBase)}&tense=${encodeURIComponent(tense)}&lang=${languageCode}&baseLang=${encodeURIComponent(baseLanguage)}`
            );

            if (!response.ok) {
                throw new Error(`Conjugation endpoint returned status ${response.status}`);
            }

            const result = await response.json();
            if (isStale()) return;
            setConjugations((prev) => ({ ...prev, [tense]: result }));
        } catch (err) {
            if (isStale()) return;
            logger.error(`ChatbotWordLookup - Error fetching conjugation for "${tense}":`, err);
            setConjugationError(err.message || 'Failed to generate conjugation');
        } finally {
            if (!isStale()) setConjugationLoading(false);
        }
    };

    const handleBackToTenses = () => {
        setActiveConjugationTense(null);
        setConjugationError(null);
    };

    // Render the body of the Conjugations accordion: either the grouped, two-column
    // list of clickable tenses, or the generated conjugation for the selected tense.
    const renderConjugationContent = () => {
        const tenseGroups = VERB_TENSES_BY_LANGUAGE[currentLanguage];
        if (!tenseGroups) return null;

        if (activeConjugationTense) {
            const data = conjugations[activeConjugationTense];
            return (
                <div className="conjugation-panel">
                    <div className="conjugation-panel-header">
                        <button
                            type="button"
                            className="conjugation-back-btn"
                            onClick={handleBackToTenses}
                        >
                            ← Tenses
                        </button>
                        <span className="conjugation-panel-title">{activeConjugationTense}</span>
                    </div>

                    {conjugationLoading && !data && (
                        <p className="conjugation-status">Loading conjugation…</p>
                    )}
                    {conjugationError && (
                        <p className="conjugation-status stories-error-text">Error: {conjugationError}</p>
                    )}

                    {data && (
                        <div className="conjugation-result">
                            {data.infinitive && (
                                <div className="conjugation-infinitive">Base form: {data.infinitive}</div>
                            )}
                            {Array.isArray(data.rows) && data.rows.length > 0 ? (
                                <table className="conjugation-table">
                                    <tbody>
                                        {data.rows.map((row, index) => (
                                            <tr key={`${row.person}-${index}`}>
                                                <td className="conjugation-person">{row.person}</td>
                                                <td className="conjugation-form">{row.form}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                !conjugationLoading && (
                                    <p className="conjugation-status">No forms available.</p>
                                )
                            )}
                            {data.notes && <p className="conjugation-notes">{data.notes}</p>}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div className="conjugation-tense-list">
                {Object.entries(tenseGroups).map(([level, tenses]) => (
                    <div key={level} className="conjugation-level-group">
                        <div className="conjugation-level-title">{level}</div>
                        <div className="conjugation-tense-grid">
                            {tenses.map((tense) => (
                                <button
                                    type="button"
                                    key={tense}
                                    className={`conjugation-tense-btn${conjugations[tense] ? ' cached' : ''}`}
                                    onClick={() => handleSelectTense(tense)}
                                >
                                    {tense}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const handleSave = async (e) => {
        e.stopPropagation();
        try {
            // Get userId using helper function
            const userId = getUserId();

            if (!userId) {
                logger.error("ChatbotWordLookup - No user ID available, cannot save word");
                throw new Error("No user ID available. Please log in again.");
            }

            // In lemma mode, save the base form (infinitive/singular); otherwise the surface
            // word. In search mode this targets the searched result, not the clicked word.
            // Always normalized (removes periods/commas, keeps apostrophes, lowercases).
            const wordToSave = activeMatchWord;

            // Extract definition text and example sentences from the currently displayed
            // result (the searched result in search mode, otherwise the clicked word).
            const defForSave = isSearchMode ? searchDefinition : definition;
            let definitionText = '';
            let exampleSentences = [];
            if (defForSave) {
                if (Array.isArray(defForSave.definitions) && defForSave.definitions.length > 0) {
                    definitionText = defForSave.definitions.join('; ');
                }
                if (Array.isArray(defForSave.examples) && defForSave.examples.length > 0) {
                    exampleSentences = defForSave.examples;
                }
            }

            logger.debugComponent('chatbot', `Saving word: "${wordToSave}" for language: ${currentLanguage} with userId: ${userId}`);

            const result = await apiClient.post('/api/saveUserWord', {
                userId,
                language: currentLanguage,
                item: wordToSave,
                lemma: activeLemma || wordToSave,
                forms: activeForms,
                definition: definitionText,
                exampleSentences: exampleSentences
            });

            // Check if result is valid (apiClient.post now returns the parsed JSON directly)
            if (!result || !result.message) {
                logger.error("ChatbotWordLookup - Failed to save word: Invalid response");
                throw new Error("Failed to save word: Invalid response");
            }

            logger.debugComponent('chatbot', `Word saved successfully:`, result.message);

            // Update context with definition and examples
            addSavedWord(wordToSave, currentLanguage, {
                grade: 1,
                known: false,
                lemma: activeLemma || wordToSave,
                forms: activeForms,
                definition: definitionText,
                exampleSentences: exampleSentences
            });

            // Set local state to force UI update immediately. In search mode this must
            // only affect the searched result, never the clicked word's highlight.
            if (isSearchMode) {
                setSearchIsSaved(true);
                setSearchGrade(1);
                setSearchKnown(false);
            } else {
                setJustSavedWord(true);

                // Set initial grade and known state
                setWordGrade(1);
                setWordKnown(false);
            }

            // Force refresh saved words to ensure the list is up to date
            refreshSavedWords();

            // Dispatch an event to notify that word data has been updated
            // This will trigger the WordCountContext to update its counts
            window.dispatchEvent(new CustomEvent('wordDataRefreshed'));
        } catch (error) {
            logger.error("ChatbotWordLookup - Error saving word:", error);
            if (isSearchMode) setSearchError("Failed to save word. Please try again.");
            else setError("Failed to save word. Please try again.");
        }
    };

    const handleDelete = async (e) => {
        e.stopPropagation();
        try {
            const userId = getUserId(); // Use helper function

            if (!userId) {
                logger.error("ChatbotWordLookup - No user ID available, cannot remove word");
                throw new Error("No user ID available. Please log in again.");
            }

            // In lemma mode, remove the base form (infinitive/singular) that was saved.
            // In search mode this targets the searched result, not the clicked word.
            const wordToDelete = activeMatchWord;

            logger.debugComponent('chatbot', `Removing saved word: "${wordToDelete}" for language: ${currentLanguage}`);

            const result = await apiClient.post('/api/removeUserWord', {
                userId,
                language: currentLanguage,
                item: wordToDelete
            });

            // Check if result is valid (apiClient.post now returns the parsed JSON directly)
            if (!result || !result.message) {
                logger.error("ChatbotWordLookup - Failed to remove word: Invalid response");
                throw new Error("Failed to remove word: Invalid response");
            }

            logger.debugComponent('chatbot', `Word removed successfully:`, result.message);

            // Update context
            removeSavedWord(wordToDelete, currentLanguage);

            // Reset the just-saved / saved state for the affected word.
            setJustSavedWord(false);
            if (isSearchMode) setSearchIsSaved(false);

            // Force refresh saved words
            refreshSavedWords();

            // Dispatch an event to notify that word data has been updated
            // This will trigger the WordCountContext to update its counts
            window.dispatchEvent(new CustomEvent('wordDataRefreshed'));
        } catch (error) {
            logger.error("ChatbotWordLookup - Error removing word:", error);
            if (isSearchMode) setSearchError("Failed to remove word. Please try again.");
            else setError("Failed to remove word. Please try again.");
        }
    };

    const handleGradeChange = async (e) => {
        e.stopPropagation();
        const newGrade = parseInt(e.target.value, 10);
        if (isSearchMode) setSearchGrade(newGrade);
        else setWordGrade(newGrade);

        if (activeIsSaved) {
            try {
                const success = await updateWordGrade(activeMatchWord, currentLanguage, newGrade);
                if (!success) {
                    logger.error("ChatbotWordLookup - Failed to update word grade");
                    setError("Failed to update word grade. Please try again.");
                }
            } catch (error) {
                logger.error("ChatbotWordLookup - Error updating word grade:", error);
                setError("Failed to update word grade. Please try again.");
            }
        }
    };

    const handleKnownChange = async (e) => {
        e.stopPropagation();
        const isKnown = e.target.checked;
        if (isSearchMode) setSearchKnown(isKnown);
        else setWordKnown(isKnown);

        if (activeIsSaved) {
            try {
                const success = await updateWordKnown(activeMatchWord, currentLanguage, isKnown);
                if (!success) {
                    logger.error("ChatbotWordLookup - Failed to update word known status");
                    setError("Failed to update word status. Please try again.");
                }
            } catch (error) {
                logger.error("ChatbotWordLookup - Error updating word known status:", error);
                setError("Failed to update word status. Please try again.");
            }
        }
    };

    const handlePlayWord = async (wordToPlay) => {
        if (isPlaying) return;
        // Stop any audio still playing from a previous word before starting a new one.
        if (activeAudioRef.current) {
            try {
                activeAudioRef.current.pause();
                activeAudioRef.current.currentTime = 0;
            } catch (e) {
                // ignore
            }
            activeAudioRef.current = null;
        }
        setIsPlaying(true);

        // Try to play cached audio first
        if (cachedAudioUrl.current) {
            try {
                const audio = new Audio(cachedAudioUrl.current);
                audio.crossOrigin = "anonymous";
                activeAudioRef.current = audio;
                audio.onended = () => { setIsPlaying(false); activeAudioRef.current = null; };
                audio.onerror = () => { setIsPlaying(false); activeAudioRef.current = null; cachedAudioUrl.current = null; };
                await audio.play();
                return;
            } catch (cacheError) {
                logger.warn("ChatbotWordLookup - Cached audio play failed, re-fetching:", cacheError);
                activeAudioRef.current = null;
                cachedAudioUrl.current = null;
            }
        }

        try {
            const payload = { text: wordToPlay, learningLanguage: currentLanguage };
            if (currentLanguage === "Spanish") {
                payload.spanishRegion = localStorage.getItem("spanishRegion") || "Latin America";
            }
            const data = await apiClient.post('/api/tts', payload, {});
            if (!data.audio) {
                logger.error("ChatbotWordLookup - No audio URL received.");
                setIsPlaying(false);
                return;
            }
            const audioUrl = data.audio.startsWith('http') ? data.audio : `${API_BASE_URL}${data.audio}`;
            cachedAudioUrl.current = audioUrl;
            const audio = new Audio(audioUrl);
            audio.crossOrigin = "anonymous";
            activeAudioRef.current = audio;
            audio.onended = () => { setIsPlaying(false); activeAudioRef.current = null; };
            audio.onerror = () => { setIsPlaying(false); activeAudioRef.current = null; cachedAudioUrl.current = null; };
            await audio.play();
        } catch (error) {
            logger.error("ChatbotWordLookup - Error playing audio:", error);
            setIsPlaying(false);
        }
    };

    const renderJapaneseDefinition = (activeWord, activeDefinition) => {
        // Shadow the outer word/definition with the values currently being displayed.
        const word = activeWord;
        const definition = activeDefinition;
        return (
            <div>
                <div className="chatbot-word-heading-row">
                    <strong>{word}</strong>
                    <button
                        className="chatbot-speaker-btn"
                        onClick={(e) => { e.stopPropagation(); handlePlayWord(word); }}
                        title="Play pronunciation"
                        disabled={isPlaying}
                    >
                        <SpeakerIcon className="chatbot-speaker-icon" />
                    </button>
                </div>
                <hr className="chatbot-definition-separator" />
                <div className="chatbot-definition-content">
                    {definition.reading && <p className="chatbot-reading">{definition.reading}</p>}
                    {definition.jlpt && <p className="chatbot-jlpt">JLPT: {definition.jlpt}</p>}
                    <ul>
                        {definition.definitions.length > 0 ?
                            definition.definitions.map((def, index) => (
                                <li key={index}>{formatDefinition(def)}</li>
                            )) :
                            <li>No definition found.</li>
                        }
                    </ul>
                </div>
            </div>
        );
    };

    const renderStandardDefinition = (activeWord, activeDefinition, useLemma) => {
        // Shadow the outer word/definition with the values currently being displayed
        // (either the normally-clicked word or the search-bar result).
        const word = activeWord;
        const definition = activeDefinition;
        // First check if we have valid data
        if (!definition) {
            return (
                <div>
                    <strong>{word}</strong>
                    <hr className="chatbot-definition-separator" />
                    <p>No definition data available.</p>
                </div>
            );
        }

        // Remove or conditionally limit the console log that's causing spam
        // Only log this when the definition is first loaded, not on every render
        if (definition && !definition.logged) {
            logger.debugComponent('chatbot', `Rendering definition for "${word}":`, definition);
            definition.logged = true;
        }

        // Group definitions by word type if they follow the format "type: definition"
        const groupedDefinitions = {};

        if (definition.definitions && definition.definitions.length > 0) {
            // Ensure all definitions are strings
            const stringDefinitions = definition.definitions.map(def =>
                typeof def === 'string' ? def : JSON.stringify(def)
            );

            stringDefinitions.forEach(def => {
                // Check if the definition includes a word type (e.g., "noun: definition")
                const match = def.match(/^([\w\s]+):(.*)/i);

                if (match) {
                    const wordType = match[1].trim().toLowerCase();
                    let definitionContent = match[2].trim();

                    // More intelligent comma-based split that respects parentheses
                    // This prevents splitting "new (plural, masculine)" into separate definitions
                    const definitions = [];
                    let currentDef = '';
                    let inParentheses = 0;

                    // Go through character by character
                    for (let i = 0; i < definitionContent.length; i++) {
                        const char = definitionContent[i];

                        // Track parentheses
                        if (char === '(') inParentheses++;
                        else if (char === ')') inParentheses--;

                        // If comma found and not inside parentheses, end definition
                        if (char === ',' && inParentheses === 0) {
                            if (currentDef.trim()) {
                                definitions.push(currentDef.trim());
                            }
                            currentDef = '';
                        } else {
                            currentDef += char;
                        }
                    }

                    // Add the last definition if there is one
                    if (currentDef.trim()) {
                        definitions.push(currentDef.trim());
                    }

                    if (!groupedDefinitions[wordType]) {
                        groupedDefinitions[wordType] = [];
                    }

                    // Add each definition individually
                    groupedDefinitions[wordType].push(...definitions);
                } else {
                    // If no word type is found, add to "other" category
                    if (!groupedDefinitions.other) {
                        groupedDefinitions.other = [];
                    }
                    groupedDefinitions.other.push(def);
                }
            });
        } else {
            // If no definitions array, create a default "other" category
            groupedDefinitions.other = ["No definition available"];
        }

        // Order of word types to display
        const wordTypeOrder = [
            'noun', 'masculine noun', 'feminine noun', 'plural noun',
            'verb', 'transitive verb', 'intransitive verb', 'reflexive verb',
            'adjective', 'adverb', 'pronoun',
            'preposition', 'conjunction', 'interjection', 'article', 'determiner', 'other'
        ];

        // Sort word types by the predefined order
        const sortedWordTypes = Object.keys(groupedDefinitions).sort((a, b) => {
            const indexA = wordTypeOrder.indexOf(a);
            const indexB = wordTypeOrder.indexOf(b);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });

        // Clean up the display word (remove periods and commas but keep apostrophes)
        const displayWord = word.replace(/[.,]/g, '');

        // In lemma mode, show the base form (infinitive/singular) as the headword so the
        // user sees the exact form that will be saved. The lemma is taken from the
        // definition currently being displayed (clicked word in lemma mode, or search result).
        const localLemma = (useLemma && definition)
            ? String(definition.lemma || definition.verb_info?.infinitive_form || '').trim()
            : '';
        const headword = localLemma ? localLemma : displayWord;

        // Check if the word is an infinitive verb
        // Normalize both the current word and the infinitive form for comparison
        const normalizedDisplayWord = normalizeWord(displayWord);
        let isInfinitiveVerb = false;

        if (definition.verb_info && definition.verb_info.infinitive_form) {
            const infinitiveForm = typeof definition.verb_info.infinitive_form === 'string'
                ? definition.verb_info.infinitive_form
                : String(definition.verb_info.infinitive_form);

            // Multiple ways to check for a match
            const normalizedInfinitive = normalizeWord(infinitiveForm);

            // Direct match
            const directMatch = (normalizedDisplayWord === normalizedInfinitive);

            // Check if the current word ends with the infinitive marker (e.g., "er", "ir", "re" in French)
            const hasInfinitiveEnding = /er$|ir$|re$/.test(normalizedDisplayWord);

            // If the word itself has an infinitive ending AND it matches the provided infinitive form
            isInfinitiveVerb = directMatch ||
                (hasInfinitiveEnding &&
                    (normalizedDisplayWord === normalizedInfinitive ||
                        normalizedDisplayWord.includes(normalizedInfinitive) ||
                        normalizedInfinitive.includes(normalizedDisplayWord)));

            if (isInfinitiveVerb) {
                logger.debugComponent('chatbot', `Word "${normalizedDisplayWord}" is detected as an infinitive verb matching "${normalizedInfinitive}"`);
            }
        }

        return (
            <div>
                <div className="chatbot-word-heading-row">
                    <strong>{headword}</strong>
                    <button
                        className="chatbot-speaker-btn"
                        onClick={(e) => { e.stopPropagation(); handlePlayWord(headword); }}
                        title="Play pronunciation"
                        disabled={isPlaying}
                    >
                        <SpeakerIcon className="chatbot-speaker-icon" />
                    </button>
                </div>
                {headword !== displayWord && (
                    <div
                        className="chatbot-lemma-subtitle"
                        style={{ fontSize: '0.85em', opacity: 0.75, marginTop: '2px' }}
                    >
                        Base form of “{displayWord}”
                    </div>
                )}
                {definition.direct_translation && (
                    <div className="chatbot-direct-translation">
                        {definition.direct_translation}
                    </div>
                )}
                {Array.isArray(definition.synonyms) && definition.synonyms.filter(Boolean).length > 0 && (
                    <div className="chatbot-synonyms">
                        <span className="chatbot-synonyms-label">Synonyms:</span>{' '}
                        {definition.synonyms.filter(Boolean).join(', ')}
                    </div>
                )}
                <hr className="chatbot-definition-separator" />

                <div className="chatbot-definition-content">
                    {sortedWordTypes.length > 0 ? (
                        // Display each word type and its definitions
                        sortedWordTypes.map(wordType => (
                            <div key={wordType} className="chatbot-word-type-section">
                                <h4 className="chatbot-word-type">{wordType !== 'other' ? wordType : ''}</h4>
                                <ul>
                                    {groupedDefinitions[wordType].map((def, index) => (
                                        <li key={index}>
                                            {typeof def === 'string' ? (
                                                // Format the definition to handle parenthetical content specially
                                                formatDefinition(def)
                                            ) : (
                                                JSON.stringify(def)
                                            )}
                                        </li>
                                    ))}
                                </ul>

                                {/* Add verb information if available and this is a verb section */}
                                {wordType.includes('verb') && definition.verb_info && (
                                    <div className="chatbot-verb-info">
                                        {isInfinitiveVerb ? (
                                            // Special display for infinitive verbs
                                            <>
                                                <p><strong>Verb form:</strong> Infinitive</p>
                                                {definition.verb_info.infinitive_form && (
                                                    <p>
                                                        <strong>Infinitive:</strong> {typeof definition.verb_info.infinitive_form === 'string' ?
                                                            definition.verb_info.infinitive_form : JSON.stringify(definition.verb_info.infinitive_form)}
                                                    </p>
                                                )}
                                            </>
                                        ) : (
                                            // Regular display for conjugated verbs
                                            <>
                                                {definition.verb_info.person && (
                                                    <p>
                                                        <strong>Person:</strong> {typeof definition.verb_info.person === 'string' ?
                                                            definition.verb_info.person : JSON.stringify(definition.verb_info.person)}
                                                    </p>
                                                )}
                                                {definition.verb_info.tense && (
                                                    <p>
                                                        <strong>Tense:</strong> {typeof definition.verb_info.tense === 'string' ?
                                                            definition.verb_info.tense : JSON.stringify(definition.verb_info.tense)}
                                                    </p>
                                                )}
                                                {definition.verb_info.infinitive_form && (
                                                    <p>
                                                        <strong>Infinitive form:</strong> {typeof definition.verb_info.infinitive_form === 'string' ?
                                                            definition.verb_info.infinitive_form : JSON.stringify(definition.verb_info.infinitive_form)}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <ul>
                            <li>No definition found.</li>
                        </ul>
                    )}

                    {/* Context accordion section */}
                    {definition.context_note && (
                        <div className="chatbot-definition-accordion">
                            <div
                                className="chatbot-definition-accordion-header"
                                onClick={() => toggleAccordion('context')}
                            >
                                <h3 className="chatbot-definition-accordion-title">Context</h3>
                                <span className="chatbot-definition-accordion-icon">
                                    {openAccordion === 'context' ? '▲' : '▼'}
                                </span>
                            </div>
                            {openAccordion === 'context' && (
                                <div className="chatbot-definition-accordion-content">
                                    <p className="chatbot-context-note">{definition.context_note}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Grammar accordion section */}
                    {definition.grammar_note && (
                        <div className="chatbot-definition-accordion">
                            <div
                                className="chatbot-definition-accordion-header"
                                onClick={() => toggleAccordion('grammar')}
                            >
                                <h3 className="chatbot-definition-accordion-title">Grammar</h3>
                                <span className="chatbot-definition-accordion-icon">
                                    {openAccordion === 'grammar' ? '▲' : '▼'}
                                </span>
                            </div>
                            {openAccordion === 'grammar' && (
                                <div className="chatbot-definition-accordion-content">
                                    <p className="chatbot-grammar-note">{definition.grammar_note}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Conjugations accordion - shown for verbs that have a curated tense list.
                        Lists tenses by level in two columns; selecting one reveals its
                        conjugation (cached, so re-opening never re-queries the model). */}
                    {definition && isVerbDefinition(definition) && VERB_TENSES_BY_LANGUAGE[currentLanguage] && (
                        <div className="chatbot-definition-accordion">
                            <div
                                className="chatbot-definition-accordion-header"
                                onClick={toggleConjugation}
                            >
                                <h3 className="chatbot-definition-accordion-title">Conjugations</h3>
                                <span className="chatbot-definition-accordion-icon">
                                    {openAccordion === 'conjugation' ? '▲' : '▼'}
                                </span>
                            </div>
                            {openAccordion === 'conjugation' && (
                                <div className="chatbot-definition-accordion-content">
                                    {renderConjugationContent()}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Examples accordion section */}
                    {definition.examples && definition.examples.length > 0 && (
                        <div className="chatbot-definition-accordion">
                            <div
                                className="chatbot-definition-accordion-header"
                                onClick={() => toggleAccordion('examples')}
                            >
                                <h3 className="chatbot-definition-accordion-title">Example Sentences</h3>
                                <span className="chatbot-definition-accordion-icon">
                                    {openAccordion === 'examples' ? '▲' : '▼'}
                                </span>
                            </div>
                            {openAccordion === 'examples' && (
                                <div className="chatbot-definition-accordion-content">
                                    <ul className="chatbot-examples-list">
                                        {definition.examples.map((example, index) => (
                                            <li key={index} className="chatbot-example-item">
                                                <p className="chatbot-example-sentence">{example.sentence}</p>
                                                <p className="chatbot-example-translation">{example.translation}</p>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderContent = () => {
        // In search mode the dictionary panel shows the result for the term the user
        // typed (which may be written in either the learning or the native language).
        // Otherwise we fall back to the normally-clicked word.
        const activeWord = isSearchMode ? searchedFor : word;
        const activeDefinition = isSearchMode ? searchDefinition : definition;
        const activeLoading = isSearchMode ? searchLoading : loading;
        const activeError = isSearchMode ? searchError : error;

        let content;

        if (activeLoading) {
            content = <p>Loading...</p>;
        } else if (activeDefinition) {
            // Search results always come back in the standard (non-Jisho) shape, so use
            // the standard renderer even for Japanese. Lemma resolution applies in search
            // mode so inflected searches show their base form as the headword.
            content = (currentLanguage === 'Japanese' && !isSearchMode)
                ? renderJapaneseDefinition(activeWord, activeDefinition)
                : renderStandardDefinition(activeWord, activeDefinition, isSearchMode ? true : lemmatizeSavedWord);
        } else if (activeError) {
            content = <p className="chatbot-error-text">Error: {activeError}</p>;
        } else {
            content = <p>No definition found.</p>;
        }

        return (
            <>
                {/* Search bar (dictionary panel only): look up another word without closing it */}
                {onSelectWord === null && (
                    <div className="chatbot-dictionary-search">
                        <input
                            type="text"
                            className="chatbot-dictionary-search-input"
                            placeholder="Search another word..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearch();
                                }
                            }}
                        />
                        {isSearchMode && (
                            <button
                                type="button"
                                className="chatbot-dictionary-search-back"
                                onClick={handleBackToWord}
                                title="Back to the original word"
                            >
                                ← Back
                            </button>
                        )}
                    </div>
                )}

                {content}

                <div className="chatbot-word-buttons">
                    {!activeIsSaved ? (
                        <button onClick={handleSave} className="chatbot-save-btn">Save Word</button>
                    ) : (
                        <button onClick={handleDelete} className="chatbot-delete-btn">Delete Word</button>
                    )}
                </div>

                {activeIsSaved && (
                    <div className="chatbot-grading">
                        <div className="difficulty-scale">
                            <div className="difficulty-labels">
                                <span className="difficulty-hard">Hard</span>
                                <span className="difficulty-easy">Easy</span>
                            </div>
                            <div className="radio-group">
                                {[1, 2, 3, 4, 5].map(grade => (
                                    <label key={grade} className={`radio-option radio-grade-${grade} ${activeGrade === grade ? 'selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="wordGrade"
                                            value={grade}
                                            checked={activeGrade === grade}
                                            onChange={handleGradeChange}
                                        />
                                        <span>{grade}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="known-checkbox">
                            <input
                                type="checkbox"
                                id="wordKnown"
                                checked={activeKnown}
                                onChange={handleKnownChange}
                            />
                            <div className="known-checkbox-info">
                                <label htmlFor="wordKnown" className="known-checkbox-label">
                                    I know this word
                                </label>
                                <div className="known-checkbox-description">
                                    Words marked as known won't be highlighted in texts
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeError && <div className="chatbot-error-text">{activeError}</div>}
            </>
        );
    };

    // Check if a character is kanji (Japanese)
    const isKanjiCharacter = (char) => {
        return /[\u4E00-\u9FFF]/.test(char);
    };

    // Get the appropriate class for a word or character
    const getWordClass = () => {
        const isSavedWord = checkSavedStatus();

        if (!isSavedWord) {
            return 'chatbot-clickable-word';
        }

        let className = 'chatbot-clickable-word chatbot-saved-word';

        // Add grade-specific class
        className += ` chatbot-grade-${wordGrade}`;

        // If word is marked as known, don't apply saved-word styling
        if (wordKnown) {
            className = 'chatbot-clickable-word';
        }

        return className;
    };

    // Render a word with appropriate styling, handling kanji characters
    const renderWordWithStyles = (text) => {
        // For languages that don't have special character handling like kanji
        if (currentLanguage !== 'Japanese') {
            return (
                <span className={getWordClass()}>
                    {text}
                </span>
            );
        }

        // Special handling for Japanese words that may contain kanji
        return (
            <span className="chatbot-clickable-word">
                {Array.from(text).map((char, idx) => {
                    // Non-kanji characters share the word's class
                    if (!isKanjiCharacter(char)) {
                        return (
                            <span key={idx} className={getWordClass()}>
                                {char}
                            </span>
                        );
                    }

                    // For kanji, check if this particular kanji is saved
                    const kanjiSaved = isKanjiSaved(char);
                    const kanjiKnown = isKanjiKnown(char);
                    const kanjiGrade = getKanjiGrade(char) || 1;

                    let kanjiClass = 'chatbot-clickable-word';

                    if (kanjiSaved) {
                        kanjiClass += ' chatbot-saved-word';
                        kanjiClass += ` chatbot-grade-${kanjiGrade}`;

                        if (kanjiKnown) {
                            kanjiClass = 'chatbot-clickable-word';
                        }
                    }

                    return (
                        <span key={idx} className={kanjiClass}>
                            {char}
                        </span>
                    );
                })}
            </span>
        );
    };

    // Auto-fetch definition when used in dictionary panel
    useEffect(() => {
        // If this component is used in the dictionary panel (onSelectWord is null),
        // automatically fetch the definition for the word
        if (onSelectWord === null && (!definition || normalizedWord !== lastClickedWord)) {
            logger.debugComponent('chatbot', `Auto-fetching definition for "${normalizedWord}" in dictionary panel`);
            setLastClickedWord(normalizedWord);
            fetchDefinition();
        }
    }, [word, onSelectWord, definition, normalizedWord, lastClickedWord]); // Include all dependencies

    // For dictionary panel display
    if (onSelectWord === null) {
        return (
            <div className="chatbot-dictionary-word-definition">
                {renderContent()}
            </div>
        );
    }

    // For clickable words in chatbot messages
    return (
        <span onClick={handleClick}>
            {renderWordWithStyles(children || word)}
        </span>
    );
};

export default ChatbotWordLookup;